import { HttpTransportType, HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";

import useAppStore from "@/store/app";
import URL, { RtcBaseUrl } from "@/services/urls";
import { debug, error, warn } from '@/log';

let initialized = false;
let connection: HubConnection;

const pendingRequests: { methodName: string, data: unknown }[] = [];

function invoke(methodName: string, data: unknown) {
  debug(`[RTC] invoke ${methodName}`);

  if (connection.state !== HubConnectionState.Connected) {
    debug(`[RTC] suspend request ${methodName}`);
    pendingRequests.push({ methodName, data });
    return;
  }

  connection.invoke(methodName, data).catch(function (err) {
    return error(err.toString());
  });
}

function invokePendingRequests() {
  if (!pendingRequests || !pendingRequests.length) { return; }
  debug(`[RTC] invoking pending requests…`);

  // retry all pending requests
  pendingRequests.splice(0, pendingRequests.length).forEach(
    ({ methodName, data }) => invoke(methodName, data)
  );
}

async function connect(cwToken: string, chatwidgetid: string) {

  const logLevel = (process.env.VUE_APP_LOGDEBUG === "true") ? LogLevel.Debug : LogLevel.Warning;
  connection = new HubConnectionBuilder()
    .configureLogging(logLevel)
    .withUrl(`${RtcBaseUrl()}?cwToken=${cwToken}&chatwidgetid=${chatwidgetid}`, {
      skipNegotiation: true,
      transport: HttpTransportType.WebSockets,
    })
    // eslint-disable-next-line no-sparse-arrays
    .withAutomaticReconnect([0,
      5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000,
      5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000,
      5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000,
      5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000,
      5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000,
      5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000,
      5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000,
      5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000,
      5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, // 10 minutes have passed
      10000, 20000, 30000, 30000, 60000, 60000, 60000, 60000, 60000, 60000, 60000, 60000,
      60000, 60000, 60000, 60000, 60000, 60000, , 60000, 60000, 60000, 60000, 60000, 60000, 60000, 60000, 60000, 60000, 60000,
      60000, 60000, 60000])
    .build();

  connection.start().then(() => {
    debug("[rtc] connection started");
    invokePendingRequests();
    useAppStore().rtcConnected = true;
  }).catch((err) => error(err));

  //--

  connection.onreconnecting(() => {
    warn("[rtc] connection stopped, trying to reconnect…");
    useAppStore().rtcConnected = false;
  });

  connection.onreconnected(() => {
    warn("[rtc] connection restored!");
    invokePendingRequests();
    useAppStore().rtcConnected = true;
  });

  connection.onclose(() => {
    warn("[rtc] connection closed.");
    useAppStore().rtcConnected = true;
  });
}

function disconnect() {
  connection.stop().then(() => {
    debug("[rtc] connection stopped.");
  });
}

function assertConnection() {
  if (initialized) { return; }

  // useAppStore().$subscribe((_, { cwToken }) => {
  //   if (cwToken) {
  //     connect(cwToken);
  //   } else {
  //     disconnect();
  //   }
  // });

  if (useAppStore().cwToken) {
    connect(useAppStore().cwToken, useAppStore().chatWidgetId);
  }

  initialized = true;
}

// ---

// eslint-disable-next-line @typescript-eslint/ban-types
export type Callback = Function;

const events: Record<string, Callback[]> = {};

export function on(eventName: string, callback: Callback) {
  debug(`[RTC] subscribe to ${eventName}`);

  assertConnection();

  if (!events[eventName]) {
    events[eventName] = [];

    connection.on(eventName, (args, arg2, arg3) => {
      debug(`[RTC:On] event ${eventName}`);
      events[eventName].forEach(cb => cb(args, arg2, arg3));
    });
  }

  events[eventName].push(callback);
}

export function off(eventName: string) {
  if (!events[eventName]) { return; }

  connection.off(eventName);
  delete events[eventName];
}

export function call<T = unknown>(methodName: string, data: T) {
  debug(`[RTC] call ${methodName}`);

  assertConnection();

  invoke(methodName, data);
}
